<script setup>
import { ref, computed, watch, markRaw } from 'vue'
import { LOG_ST_EMBARQUE } from '@/constants/LOG_ST_EMBARQUE'
import { PIPELINE_STAGES } from '@/utils/statusPipeline'
import { useActiveScreen } from '@/composables/useActiveScreen'
import ComexDrilldownLayout from '@/components/base/fluxo/ComexDrilldownLayout.vue'
import EmbarquesMateriaisForm from '@/components/logistica/embarques/EmbarquesMateriaisForm.vue'
import EmbarquesValidacaoLpcoForm from '@/components/logistica/validacao/EmbarquesValidacaoLpcoForm.vue'
import EmbarquesForm from '@/components/logistica/embarques/EmbarquesForm.vue'
import EstocagemPlaceholder from '@/components/base/fluxo/EstocagemPlaceholder.vue'
import DocAduaneiraPlaceholder from '@/components/base/fluxo/DocAduaneiraPlaceholder.vue'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  tipoOperacao: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['item-atualizado', 'cancelar', 'show-notification', 'changesDetected'])

// Mantém uma referência reativa local do item para edição,
// permitindo que o drilldown atualize o objeto sem perder a reatividade
// quando o item-atualizado é emitido pelo ComexDrilldownLayout.
// Isso garante que o pipelinePropsValue receba o lstStatusEmbarque mais recente.
const itemLocal = ref(null)

watch(() => props.itemParaEditar, (novo) => {
  itemLocal.value = novo ? { ...novo } : null
}, { immediate: true })

const handleItemAtualizado = (item) => {
  itemLocal.value = item
  // NÃO propaga item-atualizado para o MainDashboard durante navegação interna do drilldown.
  // O MainDashboard.handleItemSalvo() fecha o painel (closeFormPanel),
  // o que interrompe a navegação entre etapas da pipeline.
  // O evento cancelar é o mecanismo correto para fechar o painel.
}

// Root-type dinâmico baseado no status do embarque:
// - LOG_ST_EMBARQUE6 (DigitandoMateriais) → tela de digitação de materiais (só smartmode)
// - LOG_ST_EMBARQUE7 (ValidandoLPCO) → tela de validação com LPCO
// - Demais → tela de embarque completa
const isDigitandoMateriais = computed(() => {
  const status = itemLocal.value?.lstStatusEmbarque
  return !status || status === LOG_ST_EMBARQUE.DigitandoMateriais
})

const isValidandoLPCO = computed(() => {
  return itemLocal.value?.lstStatusEmbarque === LOG_ST_EMBARQUE.ValidandoLpcoPre
})

const rootType = computed(() => {
  if (isDigitandoMateriais.value) return 'materiais'
  if (isValidandoLPCO.value) return 'validacao'
  return 'embarque'
})

const componentMap = {
  materiais: {
    component: markRaw(EmbarquesMateriaisForm),
    color: 'var(--bs-warning)',
    icon: 'bi-inboxes',
    title: 'Digitação de Materiais'
  },
  validacao: {
    component: markRaw(EmbarquesValidacaoLpcoForm),
    color: 'var(--bs-warning)',
    icon: 'bi-shield-check',
    title: 'Validação LPCO'
  },
  embarque: {
    component: markRaw(EmbarquesForm),
    color: 'var(--bs-info)',
    icon: 'bi-ship',
    title: 'Embarque'
  },
  estocagem: {
    component: markRaw(EstocagemPlaceholder),
    color: 'var(--bs-success)',
    icon: 'bi-box-seam',
    title: 'Estocagem'
  },
  aduana: {
    component: markRaw(DocAduaneiraPlaceholder),
    color: 'var(--bs-danger)',
    icon: 'bi-shield-check',
    title: 'Doc. Aduaneira'
  }
}

const pipelineStages = PIPELINE_STAGES

// ============================================================
// Registro de formulário ativo para o resolvedor de tela
// O RAG consulta useActiveScreen().activeForm para saber qual
// documentação carregar. Como o EmbarquesCadastro alterna entre
// vários forms conforme o estágio da pipeline, registramos
// dinamicamente via watch(rootType).
// ============================================================
const { setActiveForm } = useActiveScreen()

watch(rootType, (type) => {
  const formMap = {
    materiais: 'EmbarquesMateriaisForm',
    validacao: 'EmbarquesValidacaoLpcoForm',
    embarque: 'EmbarquesForm'
  }
  setActiveForm(formMap[type] || 'EmbarquesForm')
}, { immediate: true })
</script>

<template>
  <ComexDrilldownLayout
    :root-type="rootType"
    :root-item="itemLocal"
    :component-map="componentMap"
    :stages="pipelineStages"
    :ocultar-paredes="true"
    :cor-etapa-ativa="'var(--bs-warning)'"
    :contexto-pai="{ tipoOperacao: props.tipoOperacao }"
    @item-atualizado="handleItemAtualizado"
    @cancelar="emit('cancelar')"
    @show-notification="(notif) => emit('show-notification', notif)"
    @changes-detected="(v) => emit('changesDetected', v)"
  />
</template>
